import { useCurrentSite } from "../../../contexts/models/SiteContext";
import { focusLocation } from "../../../events/actions";
import { formatAddress } from "../../../events/utils";
import EventMap from "../../../map/Map";
import MapContainer from "../../../map/MapContainer";
import { WebsiteEvent } from "../../../resources/modelTypes";
import { SelectedRecord } from "../../../resources/selectors";
import { Button } from "@minted/fancyclothes";
import config from "config";
import React, { memo, useCallback, useMemo, useState } from "react";
import { connect, useDispatch } from "react-redux";

const googleMapsApiKey = config.get("googleMapsApiKey");

const noOp = () => null;

export type DisplayMapProps = {
  events: SelectedRecord<WebsiteEvent>[];
};

type DisplayMapPropsWithRedux = {
  focusedLocationIndex: number | null;
} & DisplayMapProps;

const DisplayMap = ({
  events,
  focusedLocationIndex,
}: DisplayMapPropsWithRedux) => {
  const dispatch = useDispatch();
  const site = useCurrentSite();
  const [showMap, setShowMap] = useState(false);
  const [isMapExpanded, setIsMapExtended] = useState(false);
  const toggleShowMap = useCallback(() => {
    setShowMap((prevVal) => !prevVal);
  }, []);
  const toggleIsMapExpanded = useCallback(() => {
    setIsMapExtended((prevVal) => !prevVal);
  }, []);

  const locations: (WebsiteEvent & { formattedAddress: string })[] = useMemo(
    () =>
      events.map((event: SelectedRecord<WebsiteEvent>) => ({
        ...event.data,
        formattedAddress: event.data.hasPhysicalAddress
          ? formatAddress(event.data)
          : "",
      })),
    [events],
  );

  return showMap ? (
    <MapContainer
      isExpanded={isMapExpanded}
      onExpandToggle={toggleIsMapExpanded}
    >
      <EventMap
        apiKey={googleMapsApiKey}
        focusedLocationIndex={focusedLocationIndex}
        focusLocation={(index: number | null) => dispatch(focusLocation(index))}
        locations={locations}
        onLocationsGeocoded={noOp}
        site={site}
      />
    </MapContainer>
  ) : (
    <div>
      <Button onClick={toggleShowMap} text="Show Map" />
    </div>
  );
};

const mapStateToProps = (state) => {
  const { focusedLocationIndex } = state.events;

  return {
    focusedLocationIndex,
  };
};

const ConnectedDisplayMap = connect(mapStateToProps)(
  DisplayMap,
) as unknown as React.FC<DisplayMapProps>;

const MemoizedDisplayMap = memo(ConnectedDisplayMap);

export { MemoizedDisplayMap as DisplayMap };
